/**
 * Project-Quotation Combined Service
 *
 * Handles the creation of projects with their initial quotation
 * This service follows the correct business flow where quotations
 * create their associated projects automatically.
 *
 * @module services/projectQuotationService
 */

import { supabase } from './supabase';
import { calculateQuotationTotals, validateQuotationData } from '../utils/calculations';
import { QUOTATION_STATUS, DEFAULT_TAX_RATE } from '../utils/constants';
import { resolveInitialProjectStatus } from './projectStatusResolver';
// #609 — procedencia del precio unitario. Gemelo de
// `mcp-server/src/utils/priceProvenance.js`.
import { PRICE_ROW_SOURCE } from '../utils/priceProvenance';
import { createDefaultMilestones } from './milestoneService';

/**
 * Create a new project with its initial quotation
 * This is the main function that handles the correct business flow
 *
 * @param {Object} data - Combined project and quotation data
 * @param {string} data.title - Project title
 * @param {string} data.client_id - Client UUID
 * @param {string} data.description - Project description
 * @param {string} [data.project_type_id] - project_types.id UUID selected by the user
 *   (#462). Passed straight through to the `projects` insert; the DB
 *   trigger (`set_project_number`) reads it back off the new row to build
 *   the "tipo" segment of project_number. Omitting it is allowed — the DB
 *   function falls back to "XX" for that segment, it does not error.
 * @param {Array} data.quotation_items - Array of quotation items
 * @param {number} data.risk_percentage - Risk percentage
 * @param {number} data.profit_percentage - Profit percentage
 * @param {number} data.tax_rate - Tax rate (defaults to 13%)
 * @param {string} data.notes - Quotation notes
 * @param {string} data.initial_status - Initial quotation status (draft or under_review)
 * @returns {Promise<{data: {project, quotation}, error}>}
 */
export const createProjectWithQuotation = async (data) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Validate required fields
    if (!data.title || !data.client_id) {
      throw new Error('El título del proyecto y el cliente son requeridos');
    }

    // Validate quotation data.
    //
    // M1 + #593: the `|| 0` that used to sit on risk/profit is gone, and so is
    // M1's refusal of the absence. Measured in prod, 24 of 35 quotations carry
    // 0/0 and nothing anywhere said so — but the fix for that is not demanding
    // the margin here. The RACI puts it in step 3, with Administración, after
    // the Líder has computed the items; demanding it at creation blocked the
    // flow without ever getting an answer.
    //
    // What survives is the distinction: an absent margin reaches the INSERT as
    // an EXPLICIT null (`?? null` below), not as an omitted key that
    // `numeric(5,2) DEFAULT 0` would silently turn into a chosen-looking zero.
    // The refusal moved to the send gate — `validateQuotationReadyToSend` in
    // quotationService, the MCP handler gate, and the DB trigger.
    const quotationData = {
      items: data.quotation_items || [],
      risk_percentage: data.risk_percentage,
      profit_percentage: data.profit_percentage,
      // #457: `??` — 0% IVA (exempt) must reach validation/calculation as 0,
      // not be swapped for 13% because it is falsy.
      tax_rate: data.tax_rate ?? DEFAULT_TAX_RATE,
    };

    const validation = validateQuotationData(quotationData);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    // Calculate a provisional total for the initial project+quotation
    // INSERT below (issue #325: this JS calc is NOT the source of truth
    // — neither row exists yet, so the DB function can't be called for
    // them. Once quotation_items are inserted further down, the
    // `recalculate_quotation_on_item_change` trigger calls the canonical
    // `calculate_quotation_totals()` DB function and overwrites
    // subtotal/tax_amount/total with the authoritative value).
    const totals = calculateQuotationTotals(
      quotationData.items,
      quotationData.risk_percentage,
      quotationData.profit_percentage,
      quotationData.tax_rate
    );

    // #462: project_number is NO LONGER generated here. The old
    // `generateProjectNumber()` computed the sequence with a non-atomic
    // `SELECT count(*) + 101` — a real race condition confirmed on
    // simultaneous project creation (see the #462 issue comment). The single
    // source of truth is now the DB's SECURITY DEFINER
    // `generate_project_number()`, invoked automatically by the
    // `set_project_number` BEFORE INSERT trigger whenever `project_number`
    // is left unset — which is exactly what happens below.

    // Start a Supabase transaction-like operation
    // Note: Supabase doesn't have native transactions via JS client,
    // so we'll handle errors and attempt cleanup if needed

    let project = null;
    let quotation = null;

    // #353: this project is created ALONGSIDE a quotation that itself lands
    // as draft/under_review (never as sent to the client — see
    // `data.initial_status` below, `handleSaveDraft`/`handleSubmitForReview`
    // in QuotationBuilder.js). The old hardcode asserted quotation_sent on
    // the project in the very same call that created its quotation as NOT
    // sent — a self-contradiction. Resolve it the same way the MCP tool and
    // the plain createProject path do; see projectStatusResolver.js.
    const initialStatus = resolveInitialProjectStatus();

    try {
      // Step 1: Create the project. project_number is intentionally omitted
      // — the `set_project_number` trigger generates it from client_id +
      // project_type_id (both set below) the moment this row lands.
      const { data: newProject, error: projectError } = await supabase
        .from('projects')
        .insert({
          title: data.title,
          description: data.description || '',
          client_id: data.client_id,
          project_type_id: data.project_type_id || null,
          status: initialStatus.status,
          quoted_amount: totals.total,
          created_by: user.id,
        })
        .select()
        .single();

      if (projectError) throw projectError;
      project = newProject;
      const projectNumber = project.project_number;

      // Step 2: Create the quotation linked to the project
      const { data: newQuotation, error: quotationError } = await supabase
        .from('quotations')
        .insert({
          project_id: project.id,
          quotation_number: projectNumber, // Same number as project
          status: data.initial_status || QUOTATION_STATUS.DRAFT,
          subtotal: totals.itemsSubtotal,
          tax_rate: quotationData.tax_rate,
          tax_amount: totals.taxAmount,
          total: totals.total,
          // #593: NULL explícito, nunca clave omitida — ver el comentario del
          // gemelo en quotationService.createQuotation.
          risk_percentage: quotationData.risk_percentage ?? null,
          profit_percentage: quotationData.profit_percentage ?? null,
          notes: data.notes || '',
          created_by: user.id,
          supervisor_id: data.supervisor_id || user.id,
          lider_id: data.lider_id || user.id,
        })
        .select()
        .single();

      if (quotationError) {
        // If quotation creation fails, delete the project
        await supabase.from('projects').delete().eq('id', project.id);
        throw quotationError;
      }
      quotation = newQuotation;

      // Step 3: Create quotation items if provided
      if (quotationData.items && quotationData.items.length > 0) {
        const itemsToInsert = quotationData.items.map((item, index) => ({
          quotation_id: quotation.id,
          item_type: item.item_type,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.unit_price,
          sort_order: index,
          // #609 — ESTE es el INSERT real del carril web: `QuotationBuilder`
          // guarda la cotización entera por acá, no por
          // `quotationService.addQuotationItem` (que no tiene ningún caller de
          // UI). El precio lo tipeó una persona en el formulario, así que es
          // 'stated' POR CONSTRUCCIÓN — no hay modelo en el medio. Se escribe
          // explícito: dejarlo en NULL lo haría indistinguible de una fila
          // anterior a #609, y este carril sí puede afirmar de dónde salió.
          price_source: item.price_source || PRICE_ROW_SOURCE.STATED,
        }));

        const { error: itemsError } = await supabase
          .from('quotation_items')
          .insert(itemsToInsert);

        if (itemsError) {
          // If items creation fails, delete both quotation and project
          await supabase.from('quotations').delete().eq('id', quotation.id);
          await supabase.from('projects').delete().eq('id', project.id);
          throw itemsError;
        }

        // The item inserts above just fired `recalculate_quotation_on_item_change`,
        // which recomputed subtotal/tax_amount/total via the canonical DB
        // function (issue #325) — those may now differ from the
        // provisional `totals` estimate above. Re-sync both the returned
        // quotation/totals and the project's quoted_amount so nothing
        // downstream is left showing the stale provisional number.
        const { data: recalculated, error: refetchError } = await supabase
          .from('quotations')
          .select('*')
          .eq('id', quotation.id)
          .single();

        if (!refetchError && recalculated) {
          quotation = recalculated;
          totals.itemsSubtotal = recalculated.subtotal;
          totals.taxAmount = recalculated.tax_amount;
          totals.total = recalculated.total;

          await supabase
            .from('projects')
            .update({ quoted_amount: recalculated.total })
            .eq('id', project.id);
          project.quoted_amount = recalculated.total;
        }
      }

      // #460: seed the fixed 6-milestone checklist now that the project
      // (and its quotation) are fully committed. Best-effort — seeded
      // after everything else succeeds so a seeding failure never leaves
      // orphan milestones behind a rolled-back project, and never undoes
      // an otherwise-successful project+quotation creation.
      let milestonesWarning = null;
      const milestonesResult = await createDefaultMilestones(project.id);
      if (milestonesResult.error) {
        console.error('Failed to seed default milestones for project', project.id, milestonesResult.error);
        milestonesWarning = 'El proyecto se creó, pero no se pudieron generar los hitos por defecto.';
      }

      // Success! Return both project and quotation
      return {
        data: {
          project,
          quotation,
          projectNumber,
          totals,
        },
        error: null,
        status_provisional: initialStatus.provisional,
        ...(initialStatus.warning ? { warning: initialStatus.warning } : {}),
        ...(milestonesWarning ? { milestones_warning: milestonesWarning } : {}),
        // M1: un 0/0 deliberado es válido, pero no callado. Mismo patrón que
        // `milestones_warning` de arriba: el servicio deja constancia y quien
        // llama decide cómo mostrarlo.
        ...(validation.warnings?.length ? { margin_warning: validation.warnings[0] } : {}),
      };

    } catch (innerError) {
      // Clean up if something went wrong
      if (project && !quotation) {
        await supabase.from('projects').delete().eq('id', project.id);
      }
      throw innerError;
    }

  } catch (error) {
    console.error('Create project with quotation error:', error);
    return {
      data: null,
      error: {
        message: error.message || 'Error al crear el proyecto con cotización',
        details: error,
      },
    };
  }
};

/**
 * Update project with quotation (for existing projects)
 * This maintains backward compatibility for editing existing quotations
 *
 * @param {string} projectId - Project ID
 * @param {string} quotationId - Quotation ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise<{data, error}>}
 */
export const updateProjectWithQuotation = async (projectId, quotationId, updates) => {
  try {
    // Update project if needed
    if (updates.title || updates.description || updates.client_id) {
      const projectUpdates = {};
      if (updates.title) projectUpdates.title = updates.title;
      if (updates.description) projectUpdates.description = updates.description;
      if (updates.client_id) projectUpdates.client_id = updates.client_id;

      const { error: projectError } = await supabase
        .from('projects')
        .update(projectUpdates)
        .eq('id', projectId);

      if (projectError) throw projectError;
    }

    // Update quotation
    if (updates.quotation_items || updates.risk_percentage !== undefined ||
        updates.profit_percentage !== undefined || updates.tax_rate !== undefined) {

      // M1 + #593: PRESERVAR, no pisar y no rechazar.
      //
      // Esta ruta corre también al editar los ÍTEMS. Con el `|| 0` original,
      // editar la lista de ítems sin reenviar el margen sobreescribía un 10/25
      // guardado con 0/0 — destruía un número que un humano ya había elegido.
      // M1 lo tapó exigiendo el margen en toda edición, lo que ahora bloquearía
      // el caso normal (el Líder carga ítems en el paso 2, antes de que exista
      // margen).
      //
      // La regla correcta es la tercera: si el campo NO viene, la clave NO se
      // manda en el UPDATE y el valor guardado sobrevive — sea 10/25, sea 0/0
      // elegido, sea NULL sin definir. Si viene un número (0 incluido), se
      // escribe. Nunca se traduce "ausente" a NULL ni a 0.
      const marginProvided = (v) => !(v === undefined || v === null || v === '');

      const { data: storedQuotation } = await supabase
        .from('quotations')
        .select('risk_percentage, profit_percentage')
        .eq('id', quotationId)
        .single();

      const effectiveRisk = marginProvided(updates.risk_percentage)
        ? updates.risk_percentage
        : storedQuotation?.risk_percentage;
      const effectiveProfit = marginProvided(updates.profit_percentage)
        ? updates.profit_percentage
        : storedQuotation?.profit_percentage;

      // Calculate a provisional total (issue #325: NOT the source of
      // truth — the items delete/insert below fires
      // `recalculate_quotation_on_item_change`, which calls the canonical
      // `calculate_quotation_totals()` DB function afterwards and
      // overwrites subtotal/tax_amount/total. This value is refreshed
      // below once the item mutations complete.)
      //
      // #593: para el CÁLCULO un margen sin definir vale 0 — el borrador
      // muestra el costo. Eso no lo escribe en la columna; la columna solo se
      // toca si el caller mandó un número (ver abajo). Mismo criterio que el
      // COALESCE de `calculate_quotation_totals` en la base.
      const totals = calculateQuotationTotals(
        updates.quotation_items || [],
        effectiveRisk ?? 0,
        effectiveProfit ?? 0,
        updates.tax_rate ?? DEFAULT_TAX_RATE  // #457: 0% is a real rate
      );

      const quotationUpdates = {
        subtotal: totals.itemsSubtotal,
        tax_amount: totals.taxAmount,
        total: totals.total,
        // #457: the WRITE — editing a 0% quotation used to persist 13% back.
        tax_rate: updates.tax_rate ?? DEFAULT_TAX_RATE,
      };

      if (marginProvided(updates.risk_percentage)) {
        quotationUpdates.risk_percentage = updates.risk_percentage;
      }
      if (marginProvided(updates.profit_percentage)) {
        quotationUpdates.profit_percentage = updates.profit_percentage;
      }

      if (updates.notes !== undefined) {
        quotationUpdates.notes = updates.notes;
      }
      if (updates.supervisor_id !== undefined) {
        quotationUpdates.supervisor_id = updates.supervisor_id;
      }
      if (updates.lider_id !== undefined) {
        quotationUpdates.lider_id = updates.lider_id;
      }

      const { data: quotation, error: quotationError } = await supabase
        .from('quotations')
        .update(quotationUpdates)
        .eq('id', quotationId)
        .select()
        .single();

      if (quotationError) throw quotationError;

      // Update project quoted amount (provisional — resynced below once
      // items are settled)
      await supabase
        .from('projects')
        .update({ quoted_amount: totals.total })
        .eq('id', projectId);

      let finalQuotation = quotation;

      // Update quotation items if provided
      if (updates.quotation_items) {
        // Delete existing items
        await supabase
          .from('quotation_items')
          .delete()
          .eq('quotation_id', quotationId);

        // Insert new items
        if (updates.quotation_items.length > 0) {
          const itemsToInsert = updates.quotation_items.map((item, index) => ({
            quotation_id: quotationId,
            item_type: item.item_type,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unit_price: item.unit_price,
            sort_order: index,
            // #609 — mismo caso que el INSERT de creación: el builder borra
            // TODAS las líneas y las vuelve a insertar, así que un admin que
            // guarda desde la web está re-tipeando (o al menos mirando) cada
            // precio de la cotización. Ese guardado es la confirmación humana
            // que destraba una línea que el agente dejó `unconfirmed`.
            price_source: item.price_source || PRICE_ROW_SOURCE.STATED,
          }));

          const { error: itemsError } = await supabase
            .from('quotation_items')
            .insert(itemsToInsert);

          if (itemsError) throw itemsError;
        }

        // The item mutations above fired `recalculate_quotation_on_item_change`
        // (issue #325's canonical DB function) — re-sync the returned
        // quotation and the project's quoted_amount to that authoritative
        // value instead of leaving the provisional one in place.
        const { data: recalculated, error: refetchError } = await supabase
          .from('quotations')
          .select('*')
          .eq('id', quotationId)
          .single();

        if (!refetchError && recalculated) {
          finalQuotation = recalculated;
          await supabase
            .from('projects')
            .update({ quoted_amount: recalculated.total })
            .eq('id', projectId);
        }
      }

      return { data: finalQuotation, error: null };
    }

    return { data: null, error: null };

  } catch (error) {
    console.error('Update project with quotation error:', error);
    return { data: null, error };
  }
};

/**
 * Check if a client exists
 * @param {string} clientId - Client UUID
 * @returns {Promise<boolean>}
 */
export const validateClient = async (clientId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, role')
      .eq('id', clientId)
      .eq('role', 'client')
      .single();

    return !error && data !== null;
  } catch (error) {
    console.error('Validate client error:', error);
    return false;
  }
};

// Project types are now managed via projectTypeService.js (DB-backed).
// This re-export is kept for backwards compatibility but is no longer the source of truth.
export { getProjectTypes } from './projectTypeService';

const projectQuotationService = {
  createProjectWithQuotation,
  updateProjectWithQuotation,
  validateClient,
};

export default projectQuotationService;